import type { ConsentService, GoogleConsentType } from "./types";

/**
 * Built-in integrations (docs/cookie-consent-plan.md §5). The owner enters an
 * id; the consent runtime loads the vendor only after consent and sends the
 * vendor's own consent signals. Cookie lists and texts are curated (nl/en/de).
 */

export interface IntegrationPreset {
  id: string;
  /** Validates the owner-entered id; empty for embed services that need no id. */
  idPattern?: RegExp;
  idLabel?: string;
  idPlaceholder?: string;
  service: Omit<ConsentService, "id" | "source">;
}

const GOOGLE = { name: "Google Ireland Ltd.", country: "IE", privacyUrl: "https://policies.google.com/privacy" };
const META = { name: "Meta Platforms Ireland Ltd.", country: "IE", privacyUrl: "https://www.facebook.com/privacy/policy/" };
const MICROSOFT = { name: "Microsoft Ireland Operations Ltd.", country: "IE", privacyUrl: "https://privacy.microsoft.com/privacystatement" };

const ANALYTICS_GCM: GoogleConsentType[] = ["analytics_storage"];
const ADS_GCM: GoogleConsentType[] = ["ad_storage", "ad_user_data", "ad_personalization"];

const months = (n: number) => ({ nl: `${n} maanden`, en: `${n} months`, de: `${n} Monate` });
const days = (n: number) => ({ nl: n === 1 ? "1 dag" : `${n} dagen`, en: n === 1 ? "1 day" : `${n} days`, de: n === 1 ? "1 Tag" : `${n} Tage` });
const SESSION = { nl: "sessie", en: "session", de: "Sitzung" };
const PERSISTENT = { nl: "tot je het verwijdert", en: "until you delete it", de: "bis Sie es löschen" };

export const INTEGRATION_PRESETS: IntegrationPreset[] = [
  {
    id: "google-analytics",
    idPattern: /^G-[A-Z0-9]{4,15}$/,
    idLabel: "Measurement ID",
    idPlaceholder: "G-XXXXXXXXXX",
    service: {
      name: "Google Analytics 4", provider: GOOGLE, category: "analytics", basis: "consent", thirdCountry: true,
      purpose: { nl: "Bezoekstatistieken: welke pagina's worden bekeken, hoe lang en via welk kanaal bezoekers binnenkomen.", en: "Visitor statistics: which pages are viewed, for how long and through which channel visitors arrive.", de: "Besucherstatistiken: welche Seiten wie lange angesehen werden und über welchen Kanal Besucher kommen." },
      dataShared: { nl: "Ingekort IP-adres, apparaat- en browsergegevens, bezochte pagina's en een unieke bezoekers-ID.", en: "Truncated IP address, device and browser data, pages visited and a unique visitor ID.", de: "Gekürzte IP-Adresse, Geräte- und Browserdaten, besuchte Seiten und eine eindeutige Besucher-ID." },
      cookies: [
        { name: "_ga", retention: months(13) },
        { name: "_ga_", pattern: true, retention: months(13) },
        { name: "_gid", retention: days(1) },
        { name: "_gat", pattern: true, retention: { nl: "1 minuut", en: "1 minute", de: "1 Minute" } },
      ],
      hosts: ["www.googletagmanager.com", "www.google-analytics.com", "region1.google-analytics.com", "analytics.google.com"],
      googleConsent: ANALYTICS_GCM,
    },
  },
  {
    id: "google-tag-manager",
    idPattern: /^GTM-[A-Z0-9]{4,12}$/,
    idLabel: "Container ID",
    idPlaceholder: "GTM-XXXXXXX",
    service: {
      name: "Google Tag Manager", provider: GOOGLE, category: "marketing", basis: "consent", thirdCountry: true,
      purpose: { nl: "Laadt meet- en marketingtags die in Tag Manager zijn ingesteld. Tags krijgen je toestemmingskeuzes mee.", en: "Loads measurement and marketing tags configured in Tag Manager. Tags receive your consent choices.", de: "Lädt in Tag Manager konfigurierte Mess- und Marketing-Tags. Tags erhalten Ihre Einwilligungsentscheidungen." },
      cookies: [],
      hosts: ["www.googletagmanager.com"],
      googleConsent: [...ANALYTICS_GCM, ...ADS_GCM],
    },
  },
  {
    id: "google-ads",
    idPattern: /^AW-\d{6,12}$/,
    idLabel: "Conversion ID",
    idPlaceholder: "AW-123456789",
    service: {
      name: "Google Ads", provider: GOOGLE, category: "marketing", basis: "consent", thirdCountry: true,
      purpose: { nl: "Meten welke advertenties tot een aanvraag of aankoop leiden en advertenties tonen aan eerdere bezoekers.", en: "Measuring which ads lead to an enquiry or purchase and showing ads to previous visitors.", de: "Messen, welche Anzeigen zu Anfragen oder Käufen führen, und Anzeigen für frühere Besucher." },
      cookies: [
        { name: "_gcl_au", retention: months(3) },
        { name: "_gcl_", pattern: true, retention: months(3) },
        { name: "IDE", domain: "doubleclick.net", retention: months(13) },
      ],
      hosts: ["www.googletagmanager.com", "googleads.g.doubleclick.net", "www.googleadservices.com"],
      googleConsent: ADS_GCM,
    },
  },
  {
    id: "meta-pixel",
    idPattern: /^\d{10,20}$/,
    idLabel: "Pixel ID",
    idPlaceholder: "123456789012345",
    service: {
      name: "Meta Pixel", provider: META, category: "marketing", basis: "consent", thirdCountry: true,
      purpose: { nl: "Advertenties op Facebook en Instagram meten en tonen aan bezoekers van deze website.", en: "Measuring ads on Facebook and Instagram and showing them to visitors of this website.", de: "Anzeigen auf Facebook und Instagram messen und Besuchern dieser Website zeigen." },
      cookies: [{ name: "_fbp", retention: months(3) }, { name: "_fbc", retention: months(3) }, { name: "fr", domain: "facebook.com", retention: months(3) }],
      hosts: ["connect.facebook.net", "www.facebook.com"],
    },
  },
  {
    id: "linkedin-insight",
    idPattern: /^\d{4,12}$/,
    idLabel: "Partner ID",
    idPlaceholder: "1234567",
    service: {
      name: "LinkedIn Insight Tag", provider: { name: "LinkedIn Ireland Unlimited Company", country: "IE", privacyUrl: "https://www.linkedin.com/legal/privacy-policy" }, category: "marketing", basis: "consent", thirdCountry: true,
      purpose: { nl: "Advertenties op LinkedIn meten en tonen aan bezoekers van deze website.", en: "Measuring LinkedIn ads and showing them to visitors of this website.", de: "LinkedIn-Anzeigen messen und Besuchern dieser Website zeigen." },
      cookies: [{ name: "li_fat_id", domain: "linkedin.com", retention: months(1) }, { name: "bcookie", domain: "linkedin.com", retention: months(12) }, { name: "li_sugr", domain: "linkedin.com", retention: months(3) }],
      hosts: ["snap.licdn.com", "px.ads.linkedin.com"],
    },
  },
  {
    id: "microsoft-uet",
    idPattern: /^\d{5,12}$/,
    idLabel: "UET tag ID",
    idPlaceholder: "12345678",
    service: {
      name: "Microsoft Advertising (UET)", provider: MICROSOFT, category: "marketing", basis: "consent", thirdCountry: true,
      purpose: { nl: "Advertenties op Bing meten en tonen aan bezoekers van deze website.", en: "Measuring Bing ads and showing them to visitors of this website.", de: "Bing-Anzeigen messen und Besuchern dieser Website zeigen." },
      cookies: [{ name: "_uetsid", retention: days(1) }, { name: "_uetvid", retention: months(13) }, { name: "MUID", domain: "bing.com", retention: months(13) }],
      hosts: ["bat.bing.com"],
    },
  },
  {
    id: "microsoft-clarity",
    idPattern: /^[a-z0-9]{6,16}$/,
    idLabel: "Project ID",
    idPlaceholder: "abcdef1234",
    service: {
      name: "Microsoft Clarity", provider: MICROSOFT, category: "analytics", basis: "consent", thirdCountry: true,
      purpose: { nl: "Inzicht in hoe bezoekers de website gebruiken, met heatmaps en sessie-opnames.", en: "Insight into how visitors use the website, with heatmaps and session recordings.", de: "Einblick in die Nutzung der Website mit Heatmaps und Sitzungsaufzeichnungen." },
      cookies: [{ name: "_clck", retention: months(12) }, { name: "_clsk", retention: days(1) }, { name: "CLID", domain: "clarity.ms", retention: months(12) }],
      hosts: ["www.clarity.ms", "c.clarity.ms"],
    },
  },
  {
    id: "hotjar",
    idPattern: /^\d{5,10}$/,
    idLabel: "Site ID",
    idPlaceholder: "1234567",
    service: {
      name: "Hotjar", provider: { name: "Hotjar Ltd.", country: "MT", privacyUrl: "https://www.hotjar.com/legal/policies/privacy/" }, category: "analytics", basis: "consent",
      purpose: { nl: "Inzicht in hoe bezoekers de website gebruiken, met heatmaps en feedback.", en: "Insight into how visitors use the website, with heatmaps and feedback.", de: "Einblick in die Nutzung der Website mit Heatmaps und Feedback." },
      cookies: [{ name: "_hjSessionUser_", pattern: true, retention: months(12) }, { name: "_hjSession_", pattern: true, retention: { nl: "30 minuten", en: "30 minutes", de: "30 Minuten" } }],
      hosts: ["static.hotjar.com", "script.hotjar.com", "vars.hotjar.com"],
    },
  },
  {
    id: "youtube",
    service: {
      name: "YouTube", provider: GOOGLE, category: "marketing", basis: "consent", thirdCountry: true,
      purpose: { nl: "Video's van YouTube afspelen op deze website. YouTube kan je kijkgedrag gebruiken voor advertenties.", en: "Playing YouTube videos on this website. YouTube may use your viewing behaviour for advertising.", de: "YouTube-Videos auf dieser Website abspielen. YouTube kann Ihr Sehverhalten für Werbung nutzen." },
      cookies: [{ name: "VISITOR_INFO1_LIVE", domain: "youtube.com", retention: months(6) }, { name: "YSC", domain: "youtube.com", retention: SESSION }, { name: "yt-remote-device-id", storage: "localStorage", domain: "youtube.com", retention: PERSISTENT }],
      hosts: ["www.youtube.com", "www.youtube-nocookie.com", "youtube.com", "youtu.be", "i.ytimg.com"],
    },
  },
  {
    id: "vimeo",
    service: {
      name: "Vimeo", provider: { name: "Vimeo.com Inc.", country: "US", privacyUrl: "https://vimeo.com/privacy" }, category: "functional", basis: "consent", thirdCountry: true,
      purpose: { nl: "Video's van Vimeo afspelen op deze website.", en: "Playing Vimeo videos on this website.", de: "Vimeo-Videos auf dieser Website abspielen." },
      cookies: [{ name: "vuid", domain: "vimeo.com", retention: months(13) }],
      hosts: ["player.vimeo.com", "vimeo.com", "i.vimeocdn.com"],
    },
  },
  {
    id: "google-maps",
    service: {
      name: "Google Maps", provider: GOOGLE, category: "functional", basis: "consent", thirdCountry: true,
      purpose: { nl: "Een interactieve kaart tonen.", en: "Showing an interactive map.", de: "Eine interaktive Karte anzeigen." },
      cookies: [{ name: "NID", domain: "google.com", retention: months(6) }],
      hosts: ["www.google.com", "maps.google.com", "maps.googleapis.com", "maps.gstatic.com"],
    },
  },
];

export function findPreset(id: string): IntegrationPreset | undefined {
  return INTEGRATION_PRESETS.find((preset) => preset.id === id);
}

/** Embed services detected from a URL (for the cms:embed block and the guard). */
export function embedServiceForUrl(url: string): { serviceId: string; embedUrl: string; thumbnail?: string } | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const host = parsed.hostname.replace(/^www\./, "").replace(/^m\./, "");
  if (host === "youtube.com" || host === "youtu.be" || host === "youtube-nocookie.com") {
    const id = host === "youtu.be" ? parsed.pathname.slice(1) : parsed.searchParams.get("v") || parsed.pathname.match(/\/(embed|shorts|live)\/([\w-]{6,})/)?.[2];
    if (!id || !/^[\w-]{6,20}$/.test(id)) return null;
    return { serviceId: "youtube", embedUrl: `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`, thumbnail: `https://i.ytimg.com/vi/${id}/hqdefault.jpg` };
  }
  if (host === "vimeo.com" || host === "player.vimeo.com") {
    const id = parsed.pathname.match(/(\d{5,12})/)?.[1];
    if (!id) return null;
    return { serviceId: "vimeo", embedUrl: `https://player.vimeo.com/video/${id}?autoplay=1&dnt=1` };
  }
  if ((host === "google.com" || host === "maps.google.com") && (parsed.pathname.startsWith("/maps") || host === "maps.google.com")) {
    const embed = parsed.pathname.startsWith("/maps/embed") ? parsed.toString() : `https://www.google.com/maps?output=embed&q=${encodeURIComponent(parsed.searchParams.get("q") || decodeURIComponent(parsed.pathname.replace(/^\/maps\/(place|search)\//, "").split("/")[0] || ""))}`;
    return { serviceId: "google-maps", embedUrl: embed };
  }
  return null;
}
